import React from "react";
import logo from "../../img/logoWithTextBlue.svg";

interface FolderCatalogViewProps {
  viewedFolder: any;
  withPrices: boolean;
  isAdmin: boolean;
  user: any;
}

const FolderCatalogView: React.FC<FolderCatalogViewProps> = ({ viewedFolder, withPrices, isAdmin, user }) => {
  const canSeeAll = isAdmin || user?.category === "gold";

  const renderBlurredDetails = (content: any) => {
    if (canSeeAll || !content) return content;

    const isPrice = typeof content === "string" && (content.includes("€") || content.includes("$"));
    const placeholder = isPrice ? "XX.XX €" : "Restricted Information";

    return (
      <span style={{ filter: "blur(5px)", userSelect: "none" }}>
        {placeholder}
      </span>
    );
  };

  const getGenderName = (id: any) => {
    switch (parseInt(id)) {
      case 1: return "MEN";
      case 2: return "WOMEN";
      case 3: return "UNISEX";
      case 4: return "KIDS";
      case 5: return "BOYS";
      case 6: return "GIRLS";
      default: return "";
    }
  };

  return (
    <div className="print_catalog_container">
      {viewedFolder.products && viewedFolder.products.map((product: any, idx: number) => (
        <div className="print_page slide_layout" key={idx}>
          {/* Slide Header */}
          <div className="slide_header">
            <div className="slide_logo">
              <img src={logo} alt="DEMCO" />
            </div>
            <div className="slide_title">
              <h1 className="product_code">{product.code} - {getGenderName(product.gender_id)}</h1>
              <p className="product_name">{product.name}</p>
            </div>
          </div>

          <div className="print_main_stack">
            {/* Images Section - Now Full Width */}
            <div className="images_section full_width">
              <div className="img_container horizontal_grid">
                 <img 
                  src={process.env.REACT_APP_STORAGE_URL + (product.front_image || (product.images?.[0]?.path))} 
                  alt="Front View" 
                />
                 <img 
                  src={process.env.REACT_APP_STORAGE_URL + (product.back_image || (product.images?.[1]?.path || product.images?.[0]?.path))} 
                  alt="Back View" 
                />
                 <img 
                  src={process.env.REACT_APP_STORAGE_URL + (product.images?.[2]?.path || (product.images?.[2]?.path || product.images?.[1]?.path || product.images?.[0]?.path))} 
                  alt="Side View" 
                />
              </div>
            </div>

            {/* Details Section - Now Horizontal Bar at Bottom */}
            <div className="details_horizontal_bar">
              <div className="desc_col">
                <p className="description_text">
                  {product.description || "High-quality sustainable garment designed for durability and comfort."}
                </p>
              </div>

              <div className="fabric_col">
                <div className="data_block">
                  <h3>FABRICS</h3>
                  <div className="fabrics_specs_grid">
                    {product.fabrics && product.fabrics.length > 0 ? (
                      product.fabrics.map((fabric: any, fIdx: number) => (
                        <div key={fIdx} className="fabric_full_detail_v">
                          <div className="f_header">
                         
                            <span className="f_type">{fabric?.fabric_type_id?.name || (product.fabric_type === "knit" ? "Knit" : "Denim")}</span>
                            {withPrices && <span className="f_mill">{fabric.fabric_mill?.name} • {fabric.name} - {fabric.color}</span>}
                          </div>
                          <div className="f_row">
                             <span className="f_label">Weight: {fabric.weight_value || "N/A"}</span>
                             <span className="f_label">Comp: {fabric.composition_value || "N/A"}</span>
                             <span className="f_label">Elasticity: {fabric.elasticity_value ? `${fabric.elasticity_value}%` : "N/A"}</span>
                          </div>
                          {withPrices && fabric?.fabric_type=== "knit" && (
                            <div className="f_row_prices">
                               <span><strong>Price/kg:</strong> {fabric.price_kg ? renderBlurredDetails(`${fabric.price_kg}€`) : "N/A"}</span>
                               <span><strong>Price/m:</strong> {fabric.price_metre ? renderBlurredDetails(`${fabric.price_metre}€`) : "N/A"}</span>
                               <span><strong>BCI:</strong> {fabric.price_bci ? renderBlurredDetails(`${fabric.price_bci}€`) : "N/A"}</span>
                               <span><strong>Org:</strong> {fabric.price_organic ? renderBlurredDetails(`${fabric.price_organic}€`) : "N/A"}</span>
                            </div>
                          )}
                          {withPrices && fabric?.fabric_type=== "denim" && (
                            <div className="f_row_prices">
                               <span><strong>Price:</strong> {fabric?.price ? renderBlurredDetails(`${Math.fround(fabric?.price).toFixed(2)} €`) : "N/A"}</span>
                            
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <span className="no_data">N/A</span>
                    )}
                  </div>
                </div>
              </div>

              {withPrices && (
                <div className="price_col">
                   {product.fabric_type === "knit" ? (
                     product.special_price ? (
                        <div className="slide_price_badge_mini">
                          <span className="label">Special Price</span>
                          <span className="value">{renderBlurredDetails(`${Math.fround(product.total_pricing).toFixed(2)} €`)}</span>
                        </div>
                     ) : (
                       <div className="multi_price_stack">
                        <div className="slide_price_badge_mini">
                          <span className="label">Total Price BCI Cotton</span>
                          <span className="value">{renderBlurredDetails(`${Math.fround(product.total_price).toFixed(2)} €`)}</span>
                        </div>
                        <div className="slide_price_badge_mini">
                          <span className="label">Total Price Organic Cotton</span>
                          <span className="value">{renderBlurredDetails(`${Math.fround(product.total_price_two).toFixed(2)} €`)}</span>
                        </div>
                       </div>
                     )
                   ) : (
                     product.total_price && (
                        <div className="slide_price_badge_mini">
                          <span className="label">Price</span>
                          <span className="value">{renderBlurredDetails(`${Math.fround(product.total_price).toFixed(2)} €`)}</span>
                        </div>
                     )
                   )}
                </div>
              )}
            </div>
          </div>

          {/* Slide Footer */}
          <div className="slide_footer">
            <div className="footer_brand">DEMCOINTER</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FolderCatalogView;
